import { useState, useEffect, useRef } from "react";
import {
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Shield,
  Users,
  UserCheck,
  Key,
} from "lucide-react";
import { SearchFilter, MetricCard, InUseDialog } from "./ui";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import RoleForm from "../../components/users/RoleForm";
import ConfirmationDialog from "../../widgets/ConfirmationModal";
import rolesService, {
  Role,
  RoleMetrics,
  CreateRoleData,
  UpdateRoleData,
  RoleListParams,
} from "../../services/roles.service";
import Loader from "../../widgets/Loader";
import Tooltip from "../../widgets/ToolTip";
import Can from "../../components/auth/Can";

function Roles() {
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [inUseInfo, setInUseInfo] = useState<{ role_name: string; message: string } | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [metrics, setMetrics] = useState<RoleMetrics | null>(null);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchFilters, setSearchFilters] = useState({
    role_name: "",
    role_key: "",
  });

  const isInitialMount = useRef(true);

  useEffect(() => {
    // SearchFilter handles its own debounce; we just refetch whenever the
    // (already-debounced) filters or pagination change.
    fetchRoles();
    isInitialMount.current = false;
  }, [currentPage, searchFilters, pageSize]);

  const fetchRoles = async () => {
    try {
      setIsLoadingRoles(true);
      const params: RoleListParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        ...(searchFilters.role_name && { role_name: searchFilters.role_name }),
        ...(searchFilters.role_key && { role_key: searchFilters.role_key }),
      };

      const response = await rolesService.getRoles(params);
      setRoles(response?.data || []);
      setMetrics(response?.metrics_data || null);
    } catch (error: any) {
      if (error.response?.data?.error?.error_description) {
        toast.error(error.response.data.error.error_description);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to fetch roles");
      }
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const handleAddRole = () => {
    setSelectedRole(null);
    setIsFormOpen(true);
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedRole(null);
  };

  const handleFormSubmit = async (data: CreateRoleData | UpdateRoleData) => {
    setIsLoading(true);
    try {
      if (selectedRole) {
        await rolesService.updateRole(
          selectedRole.role_key,
          data as UpdateRoleData,
        );
        handleCloseForm();
        toast.success("Role updated successfully!");
        await fetchRoles();
      } else {
        await rolesService.createRole(data as CreateRoleData);
        handleCloseForm();
        toast.success("Role created successfully!");
        await fetchRoles();
      }
    } catch (error: any) {
      console.error("Error saving role:", error);
      if (error?.response?.data?.error?.error_description) {
        toast.error(error?.response?.data?.error?.error_description);
      } else if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      } else {
        toast.error(
          selectedRole ? "Failed to update role" : "Failed to create role",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (role: Role) => {
    setRoleToDelete(role);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (roleToDelete) {
      try {
        await rolesService.deleteRole(roleToDelete.role_key);
        setRoleToDelete(null);
        setShowDeleteConfirm(false);
        toast.success("Role deleted successfully!");
        await fetchRoles();
      } catch (error: any) {
        console.error("Error deleting role:", error);
        const errInfo = error?.response?.data?.error;
        // Backend signals "role still assigned to users" via error_code: "role_in_use"
        // (HTTP 409). Surface that as a dedicated dialog instead of a toast so the
        // operator gets a clear, dismissable explanation.
        if (errInfo?.error_code === "role_in_use") {
          const name = roleToDelete?.role_name || roleToDelete?.role_key || "this role";
          setShowDeleteConfirm(false);
          setInUseInfo({
            role_name: name,
            message: errInfo?.error_description || "This role is still in use.",
          });
          setRoleToDelete(null);
        } else if (errInfo?.error_description) {
          toast.error(errInfo.error_description);
        } else if (error?.response?.data?.message) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Failed to delete role");
        }
      }
    }
  };

  const handleCancelDelete = () => {
    setRoleToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Pagination is driven off metrics_data.total_roles (the unfiltered total).
  // When a search filter is active we fall back to the visible row count so the
  // pager doesn't claim more pages than the filter actually returns.
  const isSearching = Boolean(
    searchFilters.role_name || searchFilters.role_key,
  );
  const totalCount = isSearching ? roles.length : (metrics?.total_roles ?? 0);
  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-slate-200 border-b mb-4">
        <div className="px-6 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Roles</h1>
              <p className="text-xs text-slate-600">Manage your user roles</p>
            </div>
            <div className="flex gap-2 items-center justify-end">
              <SearchFilter
                placeholder="Search by role name..."
                value={searchFilters.role_name}
                onChange={(v) => handleSearchChange("role_name", v)}
                className="w-64"
                debounceMs={400}
              />

              <Can permission="roles:create">
              <button
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                onClick={handleAddRole}
                type="button"
              >
                <Plus className="w-4 h-4" />
                Add Role
              </button>
              </Can>
            </div>
          </div>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            icon={<Shield className="w-5 h-5" />}
            label="Total Roles"
            value={metrics?.total_roles ?? 0}
            accent="blue"
            isLoading={isLoadingRoles && !metrics}
          />
          <MetricCard
            icon={<UserCheck className="w-5 h-5" />}
            label="Roles with Users"
            value={metrics?.roles_with_users ?? 0}
            sub={
              metrics
                ? `${Math.round(
                    metrics.total_roles
                      ? (metrics.roles_with_users / metrics.total_roles) * 100
                      : 0,
                  )}% of all roles`
                : undefined
            }
            accent="emerald"
            isLoading={isLoadingRoles && !metrics}
          />
          <MetricCard
            icon={<Users className="w-5 h-5" />}
            label="Users with Roles"
            value={metrics?.total_users_with_roles ?? 0}
            accent="violet"
            isLoading={isLoadingRoles && !metrics}
          />
        </div>
      </div>

      {/* Roles Table */}
      <div className="pb-6">
        <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role Key
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permission Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {isLoadingRoles ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <Loader />
                  </td>
                </tr>
              ) : roles.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No roles found. Click "Add Role" to create your first role.
                  </td>
                </tr>
              ) : (
                roles.map((role) => {
                  const permCount = role.permissions?.length ?? 0;
                  return (
                    <tr
                      key={role._id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {role.role_name}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Updated:{" "}
                            {role.createdAt
                              ? new Date(role.createdAt).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )
                              : "—"}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <code className="text-[11px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                          {role.role_key}
                        </code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Tooltip content="Permissions count">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium rounded-full ${
                              permCount > 0
                                ? "bg-blue-50 text-blue-700"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            <Key className="w-3 h-3" />
                            {permCount}
                          </span>
                        </Tooltip>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                        <div className="flex items-center justify-start gap-1">
                          <Can permission="roles:edit">
                          <Tooltip content="Manage permissions">
                            <button
                              onClick={() =>
                                navigate(`/roles/${role.role_key}/permissions`)
                              }
                              className="text-emerald-600 hover:bg-emerald-50 p-2 rounded-md transition-colors"
                              type="button"
                            >
                              <ShieldCheck className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          </Can>
                          <Can permission="roles:edit">
                          <Tooltip content="Edit">
                            <button
                              onClick={() => handleEditRole(role)}
                              className="text-blue-600 hover:bg-blue-50 p-2 rounded-md transition-colors"
                              type="button"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          </Can>
                          <Can permission="roles:delete">
                          <Tooltip content="Delete">
                            <button
                              onClick={() => handleDeleteClick(role)}
                              className="text-red-600 hover:bg-red-50 p-2 rounded-md transition-colors"
                              type="button"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          </Can>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{startItem}</span> to <span className="font-medium">{endItem}</span> of <span className="font-medium">{totalCount}</span> results
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Show:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              
              {totalPages > 1 && (
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2)
                      pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === pageNum
                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <RoleForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        role={selectedRole as any}
        onSubmit={handleFormSubmit}
        isLoading={isLoading}
      />

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Role"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        description={`Are you sure you want to delete the role <strong>${roleToDelete?.role_name}</strong>? This action cannot be undone.`}
        iconName="warning"
      />

      <InUseDialog
        isOpen={!!inUseInfo}
        onClose={() => setInUseInfo(null)}
        title={`Cannot delete "${inUseInfo?.role_name ?? ""}"`}
        message={inUseInfo?.message || ""}
        hints={[
          "Open the Users page and reassign affected users to a different role.",
          "Then return here and try the delete again.",
        ]}
      />
    </div>
  );
}

export default Roles;
